import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/use-toast';
import { Search, Save, Edit2, X, Check, AlertCircle, Users } from 'lucide-react';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

export default function AccountNameManager() {
  const [searchTerm, setSearchTerm] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const [accountData, peopleData] = await Promise.all([
        AccountFinal.list(),
        PersonFinal.list()
      ]);

      // Count personas per account
      const personaCounts = {};
      peopleData.forEach(person => {
        const accountName = person.account_name?.toLowerCase().trim();
        if (accountName) {
          personaCounts[accountName] = (personaCounts[accountName] || 0) + 1;
        }
      });

      const enrichedAccounts = accountData.map(acc => ({
        ...acc,
        personaCount: personaCounts[acc.account_name?.toLowerCase().trim()] || 0
      }));

      setAccounts(enrichedAccounts);
      toast({ title: 'Accounts loaded', description: `Found ${accountData.length} accounts` });
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast({ title: 'Error', description: 'Failed to load accounts', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (account) => {
    setEditingId(account.id);
    setNewName(account.account_name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setNewName('');
  };

  const saveAccountName = async (account) => {
    if (!newName.trim()) {
      toast({ title: 'Error', description: 'Account name cannot be empty', variant: 'destructive' });
      return;
    }

    if (newName.trim() === account.account_name) {
      cancelEdit();
      return;
    }

    setIsSaving(true);
    try {
      const oldName = account.account_name;
      const trimmedNewName = newName.trim();

      toast({
        title: 'Processing...',
        description: `Updating account and ${account.personaCount} linked personas`,
        duration: 10000
      });

      // 1. Update the account
      await AccountFinal.update(account.id, { account_name: trimmedNewName });

      // 2. Find and update all linked personas
      const allPeople = await PersonFinal.list();
      const linkedPeople = allPeople.filter(p => 
        p.account_name?.toLowerCase().trim() === oldName.toLowerCase().trim()
      );

      console.log(`Found ${linkedPeople.length} personas to update`);

      let successCount = 0;
      let errorCount = 0;

      for (const person of linkedPeople) {
        try {
          await PersonFinal.update(person.id, { account_name: trimmedNewName });
          successCount++;

          if (successCount < linkedPeople.length) {
            await new Promise(resolve => setTimeout(resolve, 700));
          }

          if (successCount % 10 === 0) {
            toast({
              title: 'Progress...',
              description: `Updated ${successCount} of ${linkedPeople.length} personas`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Error updating persona ${person.id}:`, error);
          errorCount++;
        }
      }

      await loadAccounts();
      cancelEdit();

      if (errorCount === 0) {
        toast({
          title: 'Success!',
          description: `Renamed "${oldName}" to "${trimmedNewName}" (${successCount} personas updated)`,
          duration: 5000
        });
      } else {
        toast({
          title: 'Partially completed',
          description: `Account renamed, ${successCount} personas succeeded, ${errorCount} failed`,
          variant: 'default',
          duration: 7000
        });
      }
    } catch (error) {
      console.error('Error saving account name:', error);
      toast({
        title: 'Error',
        description: `Failed to rename account: ${error.message}`,
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredAccounts = accounts.filter(acc => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return acc.account_name?.toLowerCase().includes(searchLower) ||
           acc.country?.toLowerCase().includes(searchLower);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Edit2 className="w-5 h-5" />
          Account Name Manager
        </CardTitle>
        <CardDescription>
          Safely rename accounts and automatically update all linked personas
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            When you rename an account, all linked personas will be automatically updated to maintain data consistency.
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label>Search Accounts</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by account name or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Button onClick={loadAccounts} disabled={isLoading} className="w-full">
          {isLoading ? 'Loading...' : 'Load Accounts'}
        </Button>

        {filteredAccounts.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Personas</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAccounts.map(account => {
                    const isEditing = editingId === account.id;
                    
                    return (
                      <tr key={account.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Input
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              className="text-sm"
                              autoFocus
                              disabled={isSaving}
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-900">
                              {account.account_name}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{account.country}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="secondary" className="flex items-center gap-1 w-fit mx-auto">
                            <Users className="w-3 h-3" />
                            {account.personaCount}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                onClick={() => saveAccountName(account)}
                                disabled={isSaving}
                                className="h-7 w-7 p-0 bg-green-600 hover:bg-green-700"
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEdit}
                                disabled={isSaving}
                                className="h-7 w-7 p-0"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEdit(account)}
                              className="h-7 w-7 p-0"
                            >
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isLoading && accounts.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Click "Load Accounts" to get started
          </div>
        )}
      </CardContent>
    </Card>
  );
}